import { deleteMedia, updateMediaMeta, fetchMediaList, fetchPublicMediaList, uploadMediaDirect } from "../api/mediaService";
import { useRef } from 'react';
import { retryApiCall } from '../../../utils/retryUtils';
import { useToastContext } from '../../../App';

export function useMarkerMediaActions(setMediaList) {
    const { showToast } = useToastContext();
    const operationInProgress = useRef(new Set());

    const fetchList = async (markerId, customSetMediaList = null, { publicUrl } = {}) => {
        if (!markerId) throw new Error("fetchList requires markerId");

        try {
            const data = await retryApiCall(
                () => publicUrl
                    ? fetchPublicMediaList(publicUrl, markerId)
                    : fetchMediaList(markerId),
                {
                    maxRetries: 2,
                    baseDelay: 500
                }
            );

            const setMediaListFn = customSetMediaList || setMediaList;
            setMediaListFn(data);
            return data;
        } catch (error) {
            console.error("Failed to fetch media list:", error);
            throw new Error("メディア一覧の取得に失敗しました。");
        }
    };

    const uploadAll = async (mediaItems, markerId, onProgress = null) => {
        if (!markerId) throw new Error("uploadAll requires markerId");

        // 重複リクエスト防止
        const operationKey = `upload_${markerId}`;
        if (operationInProgress.current.has(operationKey)) {
            console.warn('Media upload operation already in progress, skipping duplicate request');
            return;
        }

        operationInProgress.current.add(operationKey);

        const uploadedItems = [];
        const failedItems = [];

        try {
            // 新規アップロード（fileプロパティがあり、urlプロパティがないもの）
            const newItems = mediaItems.filter((item) => item.file && !item.url);

            if (newItems.length > 0) {
                // 並列アップロードでも競合しないよう、完了したインデックスを Set で管理
                const completedIndices = new Set();

                // 進捗更新関数
                const updateProgress = () => {
                    if (!onProgress) return;

                    const completedFiles = completedIndices.size;
                    let currentFileName = null;
                    for (let i = 0; i < newItems.length; i++) {
                        if (!completedIndices.has(i)) {
                            currentFileName = newItems[i]?.file?.name || null;
                            break;
                        }
                    }

                    onProgress({
                        totalFiles: newItems.length,
                        completedFiles,
                        currentFileName,
                        overallProgress: (completedFiles / newItems.length) * 100
                    });
                };

                // 各ファイルを個別にアップロード（タイトル・説明を含めるため）
                const uploadPromises = newItems.map(async (item, index) => {
                    try {
                        const mediaName = (item.name && item.name.trim() !== '')
                            ? item.name
                            : item.file.name;

                        const result = await uploadMediaDirect(
                            item.file,
                            markerId,
                            mediaName,
                            item.description || "",
                            item.mediaType || "image"
                        );

                        completedIndices.add(index);
                        uploadedItems.push({ item, result });
                        updateProgress();
                        return { item, result };
                    } catch (error) {
                        completedIndices.add(index);
                        console.error(`Failed to upload ${item.file.name}:`, error);
                        failedItems.push({ item, error });

                        const errorMessage = error.message || `ファイル「${item.file.name}」のアップロードに失敗しました。`;
                        showToast(errorMessage, 'error');
                        updateProgress();
                        throw error;
                    }
                });

                // 全アップロードの完了を待機
                try {
                    await Promise.all(uploadPromises);

                    // メディアリストを更新し、他のコンポーネントに通知
                    try {
                        await fetchList(markerId);
                        window.dispatchEvent(new CustomEvent('mediaListUpdated', {
                            detail: { markerId }
                        }));
                    } catch (error) {
                        console.warn('Failed to refresh media list after uploads:', error);
                    }
                } catch (error) {
                    console.error("Some new file uploads failed:", error);
                }
            }

            // 成功したアップロードがある場合は成功通知
            if (uploadedItems.length > 0) {
                const successMessage = uploadedItems.length === 1
                    ? `メディア「${uploadedItems[0].item.name || uploadedItems[0].item.file?.name}」のアップロードが完了しました`
                    : `${uploadedItems.length}件のメディアのアップロードが完了しました`;
                showToast(successMessage, 'success');
            }

            // 失敗したアイテムがある場合はエラーを投げる
            if (failedItems.length > 0) {
                const errorMessage = `一部のメディアの処理に失敗しました: ${failedItems.map(f => f.item.name || f.item.id).join(', ')}`;
                showToast(errorMessage, 'error');
                throw new Error(errorMessage);
            }
        } catch (error) {
            console.error("Failed to upload all media:", error);

            const errorMessage = error.message || "メディアのアップロードに失敗しました。";
            if (!failedItems.some(f => f.error?.message === errorMessage)) {
                showToast(errorMessage, 'error');
            }

            // エラー時は最新の状態を取得
            try {
                await fetchList(markerId);
            } catch (refreshError) {
                console.warn('Failed to refresh media list after error:', refreshError);
            }

            throw error;
        } finally {
            operationInProgress.current.delete(operationKey);
        }
    };

    const deleteOne = async (mediaId, markerId) => {
        if (!markerId) throw new Error("deleteOne requires markerId");

        // 重複リクエスト防止
        const operationKey = `delete_${mediaId}`;
        if (operationInProgress.current.has(operationKey)) {
            console.warn('Media delete operation already in progress, skipping duplicate request');
            return;
        }

        operationInProgress.current.add(operationKey);

        try {
            // 楽観的更新: 即座にUIから削除
            setMediaList(prevList => prevList.filter(item => item.id !== mediaId));

            await retryApiCall(
                () => deleteMedia(mediaId),
                { maxRetries: 2, baseDelay: 500 }
            );

            // バックエンドから最新データを取得して同期
            try {
                await fetchList(markerId);
            } catch (refreshError) {
                console.warn('Failed to refresh media list after delete, but operation succeeded:', refreshError);
            }
        } catch (error) {
            console.error(`Media deletion failed - Media ID: ${mediaId}, Error:`, error);

            // エラー時は最新の状態を取得してUIを同期
            try {
                await fetchList(markerId);
            } catch (refreshError) {
                console.warn('Failed to refresh media list after delete error:', refreshError);
            }

            throw new Error("メディアの削除に失敗しました。");
        } finally {
            operationInProgress.current.delete(operationKey);
        }
    };

    return {
        fetchList,
        uploadAll,
        deleteOne,
        updateMediaMeta
    };
}
